import React from 'react';
import { oohData } from '../utils/oohData';
import { NetworkIcon, LinkIcon, LayoutIcon, AlertTriangleIcon } from 'lucide-react';
type Level = 'low' | 'moderate' | 'high';
const LEVEL_STYLES: Record<Level, {
  chip: string;
  dot: string;
  filled: number;
}> = {
  low: {
    chip: 'bg-green-100 text-green-800 border-green-200',
    dot: 'bg-green-500',
    filled: 1
  },
  moderate: {
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    filled: 2
  },
  high: {
    chip: 'bg-red-100 text-red-800 border-red-200',
    dot: 'bg-red-500',
    filled: 3
  }
};
const RISK_ICONS = [LinkIcon, LayoutIcon, AlertTriangleIcon];
const SeverityMeter: React.FC<{
  level: Level;
}> = ({
  level
}) => {
  const {
    dot,
    filled
  } = LEVEL_STYLES[level];
  return <div className="flex items-center space-x-1" aria-hidden="true" data-id="element-214">
      {[0, 1, 2].map(i => <span key={i} className={`w-5 h-1.5 rounded-full ${i < filled ? dot : 'bg-gray-200'}`} data-id="element-215" />)}
    </div>;
};
export const SystemDynamics: React.FC = () => {
  const {
    interactionSummary,
    risks
  } = oohData.systemDynamics;
  return <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-216">
      <div className="flex items-center space-x-3 mb-3" data-id="element-217">
        <div className="p-2 bg-[#1E90FF]/10 text-[#1E90FF] rounded-lg" data-id="element-218">
          <NetworkIcon className="w-5 h-5" data-id="element-219" />
        </div>
        <h2 className="text-lg font-medium text-gray-800" data-id="element-220">
          How the Drivers Interact
        </h2>
      </div>

      {/* Interaction narrative — the "why" behind the risks below */}
      <p className="text-sm text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-221">
        {interactionSummary}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-222">
        {risks.map((risk, idx) => {
        const Icon = RISK_ICONS[idx] ?? AlertTriangleIcon;
        const styles = LEVEL_STYLES[risk.level];
        return <div key={risk.label} className="p-5 rounded-xl border border-gray-100 bg-gray-50/50 flex flex-col" data-id="element-223">
              {/* Header: icon + title */}
              <div className="flex items-center space-x-3 mb-3" data-id="element-224">
                <div className="p-2 bg-white border border-gray-100 text-gray-500 rounded-lg" data-id="element-225">
                  <Icon className="w-5 h-5" data-id="element-226" />
                </div>
                <h3 className="font-semibold text-gray-900 leading-tight" data-id="element-227">
                  {risk.label}
                </h3>
              </div>

              {/* Severity: badge + meter */}
              <div className="flex items-center justify-between mb-3" data-id="element-228">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${styles.chip}`} data-id="element-229">
                  {risk.level}
                </span>
                <SeverityMeter level={risk.level} data-id="element-230" />
              </div>

              {/* Plain-language definition */}
              <p className="text-xs font-medium text-gray-500 italic mb-2" data-id="element-231">
                {risk.definition}
              </p>

              {/* The reason */}
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-232">
                {risk.reason}
              </p>
            </div>;
      })}
      </div>
    </div>;
};